<template>
	<div>
		<v-navigation-drawer
			:floating="true"
			:clipped="$vuetify.breakpoint.lgAndUp"
			v-model="drawer"
			fixed
			app
			width="100"
			class="primary nav-links-vertical"
		>
			<v-list
				three-line
				class="white--text nav-links"
			>
				<v-list-tile
					v-for="item in items"
					:key="item.text"
					:to="item.url"
					active-class="secondary"
				>
					<v-layout
						class="text-xs-center"
						row
						align-center
					>
						<v-flex xs12>
							<v-icon
								dark
								x-large
							>
								{{ item.icon }}
							</v-icon>
							<p class="body-2 ma-0 nav-text">{{ item.text }}</p>
						</v-flex>
					</v-layout>
				</v-list-tile>
				<v-list-tile
					v-if="$store.get('superUser')"
					to="/super"
					active-class="secondary"
				>
					<v-layout
						class="text-xs-center"
						row
						align-center
					>
						<v-flex xs12>
							<v-icon
								dark
								x-large
							>
								supervisor_account
							</v-icon>
							<p class="body-2 ma-0">Super User</p>
						</v-flex>
					</v-layout>
				</v-list-tile>
			</v-list>
			<v-speed-dial
				v-model="showContactLinks"
				direction="top"
			>
				<v-btn
					slot="activator"
					v-model="showContactLinks"
					fab
					flat
					dark
				>
					<v-icon>contact_support</v-icon>
					<v-icon>close</v-icon>
				</v-btn>
				<v-btn
					v-for="link in contactLinks"
					:key="link.icon"
					:href="link.link"
					:target="link.target"
					:color="link.color"
					fab
					small
					dark
				>
					<v-icon>{{ link.icon }}</v-icon>
				</v-btn>
			</v-speed-dial>
		</v-navigation-drawer>
		<v-toolbar
			:clipped-left="$vuetify.breakpoint.lgAndUp"
			color="primary"
			dark
			flat
			app
			fixed
		>
			<v-toolbar-side-icon
				v-if="!drawer"
				@click.stop="drawer = !drawer"
			/>
			<v-toolbar-title>
				<v-layout
					align-center
					class="pointer"
					@click="$router.push('/')"
				>
					<span class="display-1">
						<span class="font-light">Report</span>
						Mule
					</span>
					<v-avatar
						class="hidden-sm-and-down ml-2"
						size="40px"
						tile
					>
						<img src="/logo.png" alt="Report Mule logo.">
					</v-avatar>
				</v-layout>
			</v-toolbar-title>
			<v-spacer/>
			<span
				v-if="!$store.get('productionDB')"
				class="display-1 error px-2 black--text"
			>
				DEV
			</span>
			<HeaderAddButton />
			<v-menu offset-y>
				<v-btn
					slot="activator"
					:icon="$vuetify.breakpoint.smAndDown"
					dark
					flat
					ripple
				>
					<template v-if="$vuetify.breakpoint.mdAndUp">
						{{ userName }}
						<v-icon right>person</v-icon>
					</template>
					<v-icon v-else>person</v-icon>
				</v-btn>
				<v-list>
					<v-list-tile :to="{ name: 'Settings' }">
						<v-list-tile-title>Settings</v-list-tile-title>
					</v-list-tile>
					<v-list-tile v-if="showBillingButton" @click="gotoBilling()">
						<v-list-tile-title>Billing</v-list-tile-title>
					</v-list-tile>
					<v-list-tile @click="logOut()">
						<v-list-tile-title>Log Out</v-list-tile-title>
					</v-list-tile>
				</v-list>
			</v-menu>
			<v-btn
				icon
				flat
				dark
				class="ml-0"
				@click="errorDialog = true"
			>
				<v-icon>bug_report</v-icon>
			</v-btn>
		</v-toolbar>
		<v-content>
			<loading :loading="loading" />
			<router-view v-if="!loading" />
		</v-content>
		<v-dialog
			v-model="errorDialog"
			hide-overlay
			max-width="800px"
		>
			<v-card v-if="errorDialog">
				<ErrorReport @close="errorDialog = false"/>
			</v-card>
		</v-dialog>
		<v-tooltip left>
			<v-btn
				v-if="$vuetify.breakpoint.mdAndUp"
				slot="activator"
				dark
				fixed
				fab
				flat
				right
				small
				class="primary error-report-btn"
				@click="errorDialog = true"
			>
				<v-icon>bug_report</v-icon>
			</v-btn>
			<span>Report an Error</span>
		</v-tooltip>
	</div>
</template>

<script>
import HeaderAddButton from '@/components/Layout/HeaderAddButton/Index.vue';
import ErrorReport from '@/components/ErrorReport';
import { User } from '@/models/User';
import { dynamic as dynamicAxios } from '@/util/axios';

export default {
	components: {
		ErrorReport,
		HeaderAddButton,
	},
	data() {
		return {
			dialog: false,
			drawer: false,
			contactLinks: [
				{ icon: 'email', link: 'mailto:support@reportmule.com', target: '', color: 'success' },
				{ icon: 'fa fa-facebook', link: 'https://www.facebook.com/ReportMule/', target: '_blank', color: 'indigo' },
				{ icon: 'fa fa-twitter', link: 'https://www.twitter.com/reportmule', target: '_blank', color: 'blue' },
			],
			showContactLinks: false,
			projects: null,
			errorDialog: false,
			uid: localStorage.getItem('id'),
			userName: localStorage.getItem('name'),
			email: localStorage.getItem('email'),
			loading: true,
		};
	},
	created() {
		if (this.$vuetify.breakpoint.lgAndUp) {
			this.drawer = true;
		}

		// New way to load
		Promise.all([
			/** @todo Probably fetch active and archive separately and move archive fetching to
			 * ArchivedProjects component and load once only when FAB open */
			this.$store.dispatch('projects/loadAll'),
			this.$store.dispatch('users/loadAll'),
		]).then((resp) => {
			dump('Main.vue', 'Projects and users loaded', resp);
		}).catch((e) => {
			/**
			 * @todo Pass error to error page
			 * @todo Maybe check error code and have different message
			 */
			this.$router.push({ name: 'Error' });
		}).finally(() => {
			this.loading = false;
		});

		this.setSession();
		this.setSuperUser();

		if (process.env.NODE_ENV !== 'development') {
			this.$bugsnag.user = {
				id: this.uid,
				name: this.userName,
				email: this.email,
			};
		}
	},
	mounted() {
		// Google Analytics
		// this.$ga.set({
		// 	userId: this.uid,
		// 	user_id: this.uid,
		// });
		// eslint-disable-next-line no-multi-assign
		const dataLayer = (window.dataLayer = window.dataLayer || []);
		dataLayer.push({
			userId: this.uid,
		});
	},
	computed: {
		items() {
			const isSingleProject = this.$store.get('projects/isSingleProject');
			const showDashboard = !this.$store.get('isSkilled', 'hide_dashboard');
			const showSearch = this.$store.get('isSkilled', 'search');

			let projectText = 'Projects';
			let projectUrl = '/projects';

			if (isSingleProject) {
				const projects = this.$store.get('projects/active');
				projectText = projects.first().name || projectText;
				projectUrl = `/projects/${projects.first().id}/reports` || projectUrl;
			}

			return [
				...(showDashboard ? [{ icon: 'dashboard', text: 'Dashboard', url: '/' }] : []),
				{ icon: 'work', text: projectText, url: projectUrl },
				...(showSearch ? [{ icon: 'search', text: 'Search', url: '/search' }] : []),
				{ icon: 'settings', text: 'Settings', url: '/settings' },
			];
		},
		showBillingButton() {
			const roles = this.$ls.get('roles');
			return roles.indexOf('creator') > -1;
		},
	},
	methods: {
		logOut() {
			localStorage.removeItem('authKey');
			sessionStorage.removeItem('authKey');
			localStorage.removeItem('name');
			localStorage.removeItem('email');
			localStorage.removeItem('permissions');
			localStorage.removeItem('roles');
			this.$router.go({ name: 'Dashboard' });
		},
		setSession() {
			// Create 'active' cookie to identify if the user is currently in a session
			let cookieVal = this.$cookies.get('active');
			let now = (new Date).getTime();

			if (cookieVal === null || now - cookieVal > 60*60*1000) {
				const user = new User({ id: this.uid });
				user.returning();
			}

			this.$cookies.set('active', now, 0);
		},
		setSuperUser() {
			const roles = this.$ls.get('roles', []);
			const isSuperUser = roles.indexOf('super_admin') > -1;
			this.$store.set('superUser', isSuperUser);
		},
		gotoBilling() {
			dynamicAxios().post(`/accounts/${this.$ls.get('accountId', -1)}/billing`, {
				return_url: window.location.href,
			}).then((resp) => {
				window.location.href = resp.data.url;
			}).catch((e) => {
				this.$notify('error', 'Failed to retrieve billing information.');
				dump(e);
			});
		},
	},
};
</script>

<style lang="scss">
html {
	overflow-x: hidden;
}
.nav-links-vertical {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	padding-bottom: 10px;
}
.nav-links {
	margin: auto 0;
	width: 100%;
	.v-list__tile {
		height: auto;
		padding-top: 10px;
		padding-bottom: 10px;
		.nav-text {
			word-break: break-word;
		}
	}
}
.contact-links {
	display: flex;
	flex-direction: column;
}
.error-report-btn {
	z-index: 500 !important;
	top: 12px;
}
.navigation-drawer__border {
	display: none;
}
.v-card__media {
	background-color: #E0E0E0;
}
.card__list {
	width: 100px;
}
.v-breadcrumbs {
	padding: 0;
	a {
		color: #ffffff;
	}
}
.font-light {
	font-weight: 300;
}
.v-alert {
	border: none;
	.v-alert__icon.v-icon, .v-alert__dismissible .v-icon {
		color: white;
	}
}
.pointer {
	cursor: pointer;
}
// Transitions
.slide-right-enter {
	opacity: 0;
	transform: translateX(-25%);
}
.slide-right-enter-to {
	opacity: 100;
	transform: translateY(0);
}
.slide-right-leave {
	opacity: 100;
	transform: translateX(0);
}
.slide-right-leave-to {
	opacity: 0;
	transform: translateX(25%);
}
.slide-right-enter-active {
	transition: all 250ms ease-out;
}
.slide-right-leave-active {
	transition: all 250ms ease-in;
}
.slide-left-enter {
	opacity: 0;
	transform: translateX(25%);
}
.slide-left-enter-to {
	opacity: 100;
	transform: translateY(0);
}
.slide-left-leave {
	opacity: 100;
	transform: translateX(0);
}
.slide-left-leave-to {
	opacity: 0;
	transform: translateX(-25%);
}
.slide-left-enter-active {
	transition: all 250ms ease-in-out;
}
.slide-left-leave-active {
	transition: all 250ms ease-in-out;
}
</style>
